/**
 * Split an ordered list into runs of consecutive items that share a group key.
 * An item whose key is `null`/`undefined` is "ungrouped" and forms (or extends)
 * a run with `key: null`. Order is never changed, and the same key appearing
 * again after a different one starts a NEW run — grouping is purely positional,
 * so callers sort first when they want one run per group.
 *
 * Shared by the grouped listboxes (`StyledSelect` option groups, the tax-rate
 * combobox's group headings) so the "where does a heading go" decision lives in
 * exactly one place.
 */
export interface ContiguousRun<T> {
  /** The shared group key, or `null` for an ungrouped run. */
  key: string | null;
  /** The run's items with their index in the ORIGINAL list. */
  items: ReadonlyArray<{ item: T; index: number }>;
}

export function contiguousRuns<T>(
  items: readonly T[],
  keyOf: (item: T) => string | null | undefined,
): ContiguousRun<T>[] {
  const runs: { key: string | null; items: { item: T; index: number }[] }[] =
    [];
  items.forEach((item, index) => {
    const key = keyOf(item) ?? null;
    const last = runs.at(-1);
    if (last !== undefined && last.key === key) {
      last.items.push({ item, index });
    } else {
      runs.push({ key, items: [{ item, index }] });
    }
  });
  return runs;
}
